import React, { createContext, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { dismissTransientAppToast } from "../utils/appToast";
import { SPACE_NAV_TRANSACTION_STARTED_EVENT, subscribeSpaceNavigationEvent } from "../services/editor/spaceNavigationEvents";
import { ToastSurface, type ToastProps } from "../components/Toast";

type Entry = { id: string; props: ToastProps };
export const PersistentToastContext = createContext<{
  show: (id: string, props: ToastProps) => void;
  dismiss: (id: string) => void;
  navigate: (path: string) => void;
} | null>(null);

/** Display ownership outlives route-local operation ownership; nothing is persisted. */
export const PersistentToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [editorRoute, setEditorRoute] = useState<boolean | null>(null);
  const path = useRef<string | undefined>(undefined);
  const navigate = useCallback((next: string) => {
    if (path.current !== undefined && path.current !== next) dismissTransientAppToast();
    path.current = next;
    setEditorRoute(next.startsWith("/editor"));
  }, []);
  useEffect(() => subscribeSpaceNavigationEvent(
    SPACE_NAV_TRANSACTION_STARTED_EVENT, dismissTransientAppToast,
  ), []);
  const show = useCallback((id: string, props: ToastProps) => {
    setEntries(current => {
      const existing = current.findIndex(entry => entry.id === id);
      return existing < 0 ? [...current, { id, props }]
        : current.map(entry => entry.id === id ? { id, props } : entry);
    });
  }, []);
  const dismiss = useCallback((id: string) => {
    setEntries(current => current.filter(entry => entry.id !== id));
  }, []);
  const value = useMemo(() => ({ show, dismiss, navigate }), [show, dismiss, navigate]);
  return (
    <PersistentToastContext.Provider value={value}>
      {children}
      {entries.map(({ id, props }) => <ToastSurface key={id} {...props} sx={editorRoute === false ? undefined : props.sx} />)}
    </PersistentToastContext.Provider>
  );
};
